import type { TeamLevel, TeamTreeNode } from "@/lib/api/types";

export const TEAM_LEVEL_LABELS: Record<TeamLevel, string> = {
  group: "Group",
  "product-line": "Product Line",
  product: "Product",
  discipline: "Discipline",
};

export const TEAM_LEVEL_ORDER: Record<TeamLevel, number> = {
  group: 0,
  "product-line": 1,
  product: 2,
  discipline: 3,
};

export const TEAM_LEVEL_SEQUENCE: TeamLevel[] = ["group", "product-line", "product", "discipline"];

export function getTeamLevelLabel(level?: TeamLevel | null): string {
  return level ? TEAM_LEVEL_LABELS[level] : "Team";
}

export function getTeamLevelBadgeClass(level?: TeamLevel | null): string {
  switch (level) {
    case "group":
      return "border-sky-500/20 bg-sky-500/10 text-sky-200";
    case "product-line":
      return "border-violet-500/20 bg-violet-500/10 text-violet-200";
    case "product":
      return "border-amber-500/20 bg-amber-500/10 text-amber-200";
    case "discipline":
      return "border-emerald-500/20 bg-emerald-500/10 text-emerald-200";
    default:
      return "border-white/[0.08] bg-white/[0.04] text-muted-foreground";
  }
}

export function getChildTeamLevel(parentLevel?: TeamLevel | null): TeamLevel | null {
  if (!parentLevel) return "group";

  const index = TEAM_LEVEL_SEQUENCE.indexOf(parentLevel);
  if (index < 0) return null;

  return TEAM_LEVEL_SEQUENCE[index + 1] ?? null;
}

export function flattenTeamTree(nodes: TeamTreeNode[]): TeamTreeNode[] {
  return nodes.flatMap((node) => [node, ...flattenTeamTree(node.children)]);
}

export function findTeamLineage(nodes: TeamTreeNode[], teamId: string): string[] {
  for (const node of nodes) {
    if (node.id === teamId) {
      return [node.id];
    }

    const childPath = findTeamLineage(node.children, teamId);
    if (childPath.length > 0) {
      return [node.id, ...childPath];
    }
  }

  return [];
}

export function countTeamsInTree(nodes: TeamTreeNode[]): number {
  return flattenTeamTree(nodes).length;
}

export function countEpicOwnerTeams(nodes: TeamTreeNode[]): number {
  return flattenTeamTree(nodes).filter((node) => node.canOwnEpics).length;
}
